import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Outlet, useLocation } from "react-router-dom";
import keycloak, {
  getCleanKeycloakRedirectUri,
  initKeycloak,
} from "../../keycloak";
import LoadingFallback from "Components/LoadingFallback/LoadingFallback";
import {
  LOGOUT_IN_PROGRESS_KEY,
  LOGOUT_IN_PROGRESS_TTL_MS,
} from "../../Redux/Actions";
// import { useWebSocketNotifications } from "hooks/useWebSocketNotifications";

const isLogoutSessionActive = () => {
  try {
    const raw = sessionStorage.getItem(LOGOUT_IN_PROGRESS_KEY);
    if (!raw) return false;
    const startedAt = Number(raw);
    if (!Number.isFinite(startedAt)) {
      sessionStorage.removeItem(LOGOUT_IN_PROGRESS_KEY);
      return false;
    }
    const active = Date.now() - startedAt < LOGOUT_IN_PROGRESS_TTL_MS;
    if (!active) {
      sessionStorage.removeItem(LOGOUT_IN_PROGRESS_KEY);
    }
    return active;
  } catch {
    return false;
  }
};

const isSharedGuestResultsPath = (location) => {
  const pathname = String(location?.pathname || "").replace(/\/+$/, "") || "/";
  if (pathname !== "/games/results") return false;
  const params = new URLSearchParams(location?.search || "");
  if (params.get("guest") === "1") return true;
  return [
    "sharedResultToken",
    "sharedResulttoken",
    "shared_result_token",
    "shared-result-token",
  ].some((key) => String(params.get(key) || "").trim());
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useSelector((state) => state.auth);
  const location = useLocation();
  const loginAttemptedRef = useRef(false);
  const allowSharedGuestResults = isSharedGuestResultsPath(location);
  // TODO: Re-enable WebSocket notifications once the backend issue with Spring Security is resolved and we can reliably test the connection. For now, leaving this out to avoid potential connection errors and console noise during development.
  //   useWebSocketNotifications({
  //     onNotification: (notification) => {
  //     },
  //     enabled: isAuthenticated,
  //   });
  const redirectUri = getCleanKeycloakRedirectUri(
    `${window.location.origin}${location.pathname}${location.search}${location.hash}`,
  );
  const logoutInProgress = !isAuthenticated && isLogoutSessionActive();

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (
      !allowSharedGuestResults &&
      !isAuthenticated &&
      !loginAttemptedRef.current &&
      !logoutInProgress
    ) {
      loginAttemptedRef.current = true;
      const initAndLogin = async () => {
        try {
          if (!keycloak.authenticated) {
            await initKeycloak();
          }
        } catch (initError) {
          console.warn(
            "ProtectedRoute → initKeycloak failed",
            initError?.message || initError,
          );
        }
        try {
          keycloak.login({ redirectUri });
        } catch (loginError) {
          console.error("ProtectedRoute → keycloak.login failed", loginError);
          loginAttemptedRef.current = false;
        }
      };
      initAndLogin();
    }
  }, [
    allowSharedGuestResults,
    isAuthenticated,
    isLoading,
    logoutInProgress,
    redirectUri,
  ]);

  if (allowSharedGuestResults) {
    return <>{children ? children : <Outlet />}</>;
  }

  if (logoutInProgress) {
    return null;
  }

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (!isAuthenticated) {
    return <LoadingFallback />;
  }

  // If children are provided, render them; otherwise, render <Outlet /> for nested routes
  return <>{children ? children : <Outlet />}</>;
};

export default ProtectedRoute;
